var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { action, observable, computed } from "mobx";
import { EventsBySportIdRequest } from "sb-bff-integration";
import Debounce from "debounce-decorator";
import { ActiveRequestsType } from "../typings";
import { EventsRequestType, FilterPredicate, MainGroupConfiguration, SubGroupConfiguration } from "../data";
import { requestBuilder, queryBuilder, DateRangeMapper, LoadedData, uniqueElementsById, FilterManager, isEntityFromType, isDifferentResponse, compareArrays, applyEventsChanges, applyLeagueChanges, applyMarketChanges } from "../helpers";
import { UpdateDebounceConfig } from "../constants";
export class Retriever {
    constructor(bff, sdapi, dataProvider, cmsConfig, cardConfig, filterManager, connectonFactory, deviceChangeDispatcherMock) {
        this.InvalidSportId = "-1";
        this.EventsPerRequestDefault = 100;
        /**
         * Execute according request for fetching data
         */
        this.loadData = () => {
            if (this.isRequestAvailable()) {
                this.sub && this.sub.unsubscribe();
                Object.keys(this.activeRequests).forEach(type => {
                    this[`loadData${this.activeRequests[type].type}`](type);
                });
            }
        };
        this.bff = bff;
        this.sdapi = sdapi;
        this.dataProvider = dataProvider;
        this.cmsConfig = cmsConfig;
        this.eventsPerRequest = dataProvider.eventsPerRequest ? dataProvider.eventsPerRequest : this.EventsPerRequestDefault;
        this.activeRequests = this.initializeRequests();
        this.deviceChangeDispatcher = deviceChangeDispatcherMock;
        this.data = { events: observable.map({}, { deep: false }), markets: observable.map({}, { deep: false }), leagues: observable.map({}, { deep: false }) };
        this.temporaryData = { events: [], markets: [], leagues: [] };
        this.subLoaders = this.initializeLoaders();
        this.filterManager = filterManager;
        this.connectonFactory = connectonFactory;
        this.regularUpdate = this.regularUpdate.bind(this);
        this.regularUpdateTimeOut = this.regularUpdateTimeOut.bind(this);
        this.processChangeResponse = this.processChangeResponse.bind(this);
        this.processDeleteResponse = this.processDeleteResponse.bind(this);
        this.processError = this.processError.bind(this);
        this.loadMoreData = this.loadMoreData.bind(this);
        this.sub = undefined;
        this.changesCache = { events: [], markets: [] };
        this.loadedData = new LoadedData();
        this.currentSports = [];
        this.isMiniEventCard = this.dataProvider.isMiniEventCard;
        this.allEventsLoaded = false;
        this.isLoadMore = false;
        this.additionalEventIds = [];
        this.shouldClearAdditionalEvents = false;
        this.sportsWithAdditionalEvents = []; //TODO: Extract view more fields into manager object (or refactor)
        this.cardConfig = cardConfig;
        this.connectionHandler = {
            byTopLeagues: {},
            bySportId: {},
            byEventId: {},
            byRegionId: {},
            byLeagueId: {},
            byCouponId: {}
        };
    }
    activate() {
        Object.keys(this.activeRequests).forEach(type => {
            this.setLoader(true, type);
        });
        this.clearData();
        this.deviceChangeDispatcher && this.deviceChangeDispatcher.activate();
    }
    deactivate() {
        Object.keys(this.activeRequests).forEach(type => {
            this.setLoader(false, type);
        });
        Object.keys(this.activeRequests).forEach(type => {
            this.subLoaders[type].isFirstLoad = true;
        });
        this.clearData();
        this.currentSports = [];
        this.sub && this.sub.unsubscribe();
        this.deviceChangeDispatcher && this.deviceChangeDispatcher.deactivate();
        this.filterManager.setMainPredicate(this.dataProvider.filterPredicate);
        Object.keys(this.connectionHandler).map(handler => {
            if (this.connectionHandler[handler].fixtures) {
                this.connectionHandler[handler].fixtures.deactivate();
            }
            if (this.connectionHandler[handler].outrights) {
                this.connectionHandler[handler].outrights.deactivate();
            }
        });
    }
    get leagues() {
        return [...this.data.leagues.values()];
    }
    setParams(sportIds) {
        Object.keys(this.activeRequests).forEach(type => {
            requestBuilder.buildIds(this.activeRequests[type].request, sportIds);
        });
        this.currentSports = sportIds;
    }
    /**
     * Update query filter to be for specific sport ids
     * @param {string[]} sportIds Newly selected sport ids
     */
    querySportUpdate(sportIds) {
        this.currentSports = sportIds;
        if (this.currentSports.length === 1 && this.currentSports[0] === this.InvalidSportId) {
            this.finishLoading();
            return;
        }
        let shouldUpdateFirstLoad = this.loadedData.updateSports(sportIds);
        Object.keys(this.activeRequests).forEach(type => {
            this.subLoaders[type].isFullLoadFinished = false;
            shouldUpdateFirstLoad && (this.subLoaders[type].isFirstLoad = true);
        });
        Object.keys(this.activeRequests).forEach(type => {
            this.setActiveRequest(type);
            let requestParams = this.getRequestParams(type);
            requestBuilder.buildSportIdsUpdate(requestParams, sportIds, this.shouldBuildSortParam());
        });
        this.filterManager.setMainPredicate(FilterPredicate.Sport);
        this.shouldClearAdditionalEvents = true;
        this.clearPagination();
        this.loadData();
    }
    /**
     * Update query filter to be for specific date range
     * @param {DateRangeProp} dateRange Newly selected date range
     */
    queryDatesUpdate(dateRange, sportIds) {
        let shouldUpdateFirstLoad = this.loadedData.updateDates(sportIds, dateRange.from || new Date());
        Object.keys(this.activeRequests).forEach(type => {
            this.subLoaders[type].isFullLoadFinished = false;
            shouldUpdateFirstLoad && (this.subLoaders[type].isFirstLoad = true);
        });
        Object.keys(this.activeRequests).forEach(type => {
            this.setActiveRequest(type);
            let requestParams = this.getRequestParams(type);
            requestBuilder.buildDateRangeUpdate(requestParams, dateRange, this.shouldBuildSortParam());
            this.activeRequests[type].request.pagination = { top: this.eventsPerRequest, skip: 0 };
        });
        this.sportsWithAdditionalEvents = [];
        this.filterManager.setMainPredicate(FilterPredicate.Date);
        this.loadData();
    }
    /**
     * Update query filter to be for specific league ids from sport ids
     * @param {string[]} leagueIds Newly selected league ids
     * @param {string[]} sportIds Newly selected sport ids
     */
    queryLeaguesUpdate(leagueIds, sportIds) {
        this.currentSports = sportIds;
        Object.keys(this.activeRequests).forEach(type => {
            this.subLoaders[type].isFullLoadFinished = false;
            this.subLoaders[type].isFirstLoad = true;
        });
        Object.keys(this.activeRequests).forEach(type => {
            this.setActiveRequest(type);
            let requestParams = this.getRequestParams(type);
            requestBuilder.buildLeagueIdsUpdate(requestParams, leagueIds, sportIds);
            this.activeRequests[type].request.pagination = { top: this.eventsPerRequest, skip: 0 };
        });
        this.filterManager.setMainPredicate(FilterPredicate.Sport);
        this.loadData();
    }
    /**
     * Update query filter to be for specific regionId ids from sport id
     * @param {string[]} regionId
     */
    queryRegionIdUpdate(regionId, sportId) {
        Object.keys(this.activeRequests).forEach(type => {
            this.subLoaders[type].isFullLoadFinished = false;
            this.subLoaders[type].isFirstLoad = true;
        });
        Object.keys(this.activeRequests).forEach(type => {
            this.setActiveRequest(type);
            let requestParams = this.getRequestParams(type);
            requestBuilder.buildRegionIdUpdate(requestParams, regionId, sportId, {
                top: this.eventsPerRequest,
                skip: 0
            });
        });
        this.filterManager.setMainPredicate(FilterPredicate.Sport);
        this.loadData();
    }
    /**
     * Update query filter to be for current tag
     * @param {string} currentTag Tag to be changed
     */
    queryTagsUpdate(currentTag) {
        let shouldUpdateFirstLoad = this.loadedData.updateTags(currentTag);
        Object.keys(this.activeRequests).forEach(type => {
            this.subLoaders[type].isFullLoadFinished = false;
            shouldUpdateFirstLoad && (this.subLoaders[type].isFirstLoad = true);
        });
        Object.keys(this.activeRequests).forEach(type => {
            this.setActiveRequest(type);
            let requestParams = this.getRequestParams(type);
            requestBuilder.buildEventTagsUpdate(requestParams, [currentTag], this.shouldBuildSortParam());
            this.activeRequests[type].request.pagination = { top: this.eventsPerRequest, skip: 0 };
        });
        this.filterManager.setMainPredicate(FilterPredicate[currentTag.toString()]);
        this.loadData();
    }
    /**
     * Update query filter to be for favourites
     * @param {string[]} eventIds Ids of favourite events
     */
    queryFavoritesUpdate(favoriteEvents) {
        this.currentSports = favoriteEvents.map(e => e.sportId);
        Object.keys(this.activeRequests).forEach(type => {
            this.subLoaders[type].isFullLoadFinished = false;
            this.subLoaders[type].isFirstLoad = true;
        });
        Object.keys(this.activeRequests).forEach(type => {
            this.setActiveRequest(type, false);
            let requestParams = this.getRequestParams(type);
            requestBuilder.buildEventIdsUpdate(requestParams, favoriteEvents);
            this.activeRequests[type].request.pagination = { top: this.eventsPerRequest, skip: 0 };
        });
        this.filterManager.setMainPredicate(FilterPredicate.Favorites);
        this.loadData();
    }
    /**
    * renew Data on JWTToken change
    */
    renewJWTToken() {
        setTimeout(this.loadData, 0);
    }
    /**
    * sets the customers regionId
    * @param {regionId} customers regionId
    */
    setRegionId(regionId) {
        Object.keys(this.activeRequests).forEach(type => {
            requestBuilder.buildRegionIds(this.activeRequests[type].request, this.dataProvider.ipCountry.concat(regionId));
        });
    }
    /**
    * removes the customers regionId
    *
    */
    removeRegionId() {
        Object.keys(this.activeRequests).forEach(type => {
            requestBuilder.buildRegionIds(this.activeRequests[type].request, this.dataProvider.ipCountry);
        });
    }
    /**
     * Helper function for updating current data and constructing new query for SportData API (request by ids)
     * @param {EventsResponse} response
     */
    buildUpdateQueryByPersonalization(response, requestType) {
        const queryParams = this.getQueryParams(requestType);
        const eventIds = this.getEventIds(response);
        this.query = queryBuilder.buildQueryByEventIdsOnly(queryParams, eventIds);
        this.updateData(queryParams.request, response, requestType);
    }
    /**
     * Get eventIds from response
     * @param {EventsResponse} response
     */
    getEventIds(response) {
        if (!response.events) {
            return [];
        }
        return response.events.reduce((arr, ev) => {
            ev.id && arr.push(ev.id);
            return arr;
        }, []);
    }
    /**
     * Subscribe for updates from SportData API
     */
    querySub() {
        this.sub = this.sdapi.subscribeForEvents(this.query);
    }
    /**
     * Handler for update events - execute add,change or remove methods
     * @param {EventPushResponse} x
     */
    regularUpdate(x) {
        Object.keys(x.data).forEach(prop => {
            let property = prop === "add" ? "addChanges" : prop;
            this[property](x.data[prop]);
        });
    }
    /**
     * Reset observable data
     */
    clearData() {
        this.data.events.clear();
        this.data.markets.clear();
        this.data.leagues.clear();
        this.temporaryData = { events: [], markets: [], leagues: [] };
        this.loadedData.clearData();
    }
    // VERY IMPORTANT:
    // SDAPI is sending one event per change
    // 20 events - 20 time triggering the update logic and render
    // batching them and executing every half a second reduces
    // CPU usage to almost none
    /**
     * Handler for "Change" event from SportData API
     * @param {EventPayload} x
     */
    change(x) {
        Object.keys(this.changesCache).map(prop => this.changesCache[prop].splice(this.changesCache[prop].length, 0, ...x[prop]));
        this.applyChanges();
    }
    applyChanges() {
        this.batchedChange(this.changesCache);
        this.changesCache = { events: [], markets: [] };
    }
    replaceLeagues(x) {
        x.leagues.forEach(league => {
            // const existingLeague = this.data.leagues.get(league.id)
            // if (existingLeague) {
            //     applyLeagueChanges(existingLeague, league)
            //     return
            // }
            this.data.leagues.set(league.id, league);
        });
    }
    /**
     * Add and update currently stored data
     * @param {EventsResponse} x
     * @param {Function} filterFn Filter predicate
     */
    updateCurrentData(x, filterFn, requestType) {
        let collections = this.filterManager.getMainFilterCollections();
        Object.keys(this.data).forEach(collection => {
            const currentData = [...this.data[collection].values()];
            const dataToRemain = collections.indexOf(collection) > -1
                ? currentData.filter(e => !filterFn(e) || !isEntityFromType(e, requestType, [...this.data.events.values()]))
                : currentData;
            uniqueElementsById(dataToRemain.concat(x[collection])).forEach(e => {
                // const collectionItem = this.data[collection].get(e.id)
                // if (collectionItem) {
                //     if (collection === "events") {
                //         applyEventsChanges(collectionItem, e)
                //         this.data[collection].set(e.id, e)
                //     }
                //     if (collection === "markets") {
                //         applyMarketChanges(collectionItem, e)
                //     }
                //     if (collection === "leagues") {
                //         applyLeagueChanges(collectionItem, e)
                //     }
                //     return
                // }
                this.data[collection].set(e.id, e);
            });
        });
    }
    /**
     * Handler for 'Remove' update event from SportData API
     * @param {EventPayload} x
     */
    remove(x) {
        Object.keys(x).forEach(prop => {
            x[prop].forEach(y => {
                this.data[prop].delete(y.id);
            });
        });
    }
    batchedChange(x) {
        Object.keys(x).forEach(prop => {
            x[prop].forEach(y => {
                if (this.data[prop].has(y.id)) {
                    const item = this.data[prop].get(y.id);
                    if (prop === "events") {
                        applyEventsChanges(item, y);
                    }
                    if (prop === "leagues") {
                        applyLeagueChanges(item, y);
                    }
                    if (prop === "markets") {
                        applyMarketChanges(item, y);
                    }
                    this.data[prop].set(item.id, Object.assign({}, item));
                }
            });
        });
    }
    /**
     * Get current filter function
     * @param {EventListPresenterRequest} request
     */
    getFilterFunction(request, requestType) {
        const ids = this.getFilterIds(request, requestType);
        let from, to;
        if (request.dateRange) {
            this.dataProvider.useNewTimeOnEveryRequest && (request.dateRange.from = new Date());
            from = request.dateRange.from;
            to = DateRangeMapper[request.dateRange.timeRange](from);
        }
        return this.filterManager.filterEntity.bind(FilterManager, ids, from, to);
    }
    /**
     * Check if market is from the required type
     * @param {string} marketId
     * @param {Market} x
     */
    filterByMarketId(marketId, x) {
        return !!marketId ? x.marketType.id === marketId : true;
    }
    /**
     * Request events by top leagues and sport ids
     */
    loadDataByTopLeagues(requestType) {
        let activeRequest = this.activeRequests[requestType].request;
        let loader = this.subLoaders[requestType];
        loader.isFirstLoad && (loader.isLoading = true);
        loader.isFullLoadFinished = false;
        this.handleAllEventsLoaded();
        if (this.connectionHandler.byTopLeagues[requestType]) {
            this.connectionHandler.byTopLeagues[requestType].deactivate(); // TODO: don't use deactive because we dont want to re pull same data again.
        }
        const onAdd = (response) => {
            this.updateRetrieverData(activeRequest, response, requestType);
            if (loader.isFirstLoad) {
                loader.isFirstLoad = false;
            }
        };
        const onInitial = (response) => {
            onAdd(response);
            this.loadEventsByTopLeagues(response, activeRequest, requestType, loader); // TODO: this could be called paralel?
        };
        this.connectionHandler.byTopLeagues[requestType] = this.connectonFactory.createEventsFromTopLeaguesBySportId({
            onAdd,
            onChange: this.processChangeResponse,
            onInitial,
            onDelete: this.processDeleteResponse,
            onError: this.processError
        });
        this.connectionHandler.byTopLeagues[requestType].loadData(activeRequest);
        if (!this.dataProvider.isTopLeaguesOnly) {
            this.loadDataBySportId(activeRequest, requestType);
        }
    }
    handleAllEventsLoaded() {
        if (!this.isLoadMore && this.sportsWithAdditionalEvents.length && this.sportsWithAdditionalEvents.indexOf(this.currentSports[0]) !== -1) {
            this.setAllEventsLoaded(false);
            this.sportsWithAdditionalEvents = [];
        }
        else {
            this.setAllEventsLoaded(true);
        }
    }
    loadEventsByTopLeagues(response, activeRequest, requestType, loader) {
        this.checkLoadedEvents(response.events);
        if (response.events.length === 0 && this.dataProvider.isTopLeaguesOnly) {
            this.updateWithTemporaryData(activeRequest, requestType);
            this.loadDataBySportId(activeRequest, requestType);
        }
        else if (this.dataProvider.isTopLeaguesOnly && response.events.length >= this.eventsPerRequest) {
            loader.isLoading = false;
            this.updateWithTemporaryData(activeRequest, requestType);
            this.loadMoreData();
        }
        else {
            loader.isLoading = false;
            this.updateWithTemporaryData(activeRequest, requestType);
        }
    }
    /**
     * Request events by sport ids
     * @param {EventsBySportIdRequest} request Request for events from regular league ids
     */
    loadDataBySportId(request, requestType) {
        if (this.connectionHandler.bySportId[requestType]) {
            this.connectionHandler.bySportId[requestType].deactivate();
        }
        const bySportIdRequest = requestBuilder.buildSportIdRequest(request, request.pagination ? request.pagination : {});
        const onAdd = (response) => {
            this.checkLoadedEvents(response.events);
            this.addDataBySportId(response, requestType);
        };
        this.connectionHandler.bySportId[requestType] = this.connectonFactory.createEventsBySportId({
            onAdd,
            onChange: this.processChangeResponse,
            onInitial: onAdd,
            onDelete: this.processDeleteResponse,
            onError: this.processError
        });
        this.connectionHandler.bySportId[requestType].loadData(bySportIdRequest);
    }
    /**
     * Request events by event ids
     */
    loadDataByEventIds(requestType) {
        let activeRequest = this.activeRequests[requestType].request;
        let loader = this.subLoaders[requestType];
        loader.isFirstLoad && (loader.isLoading = true);
        loader.isFullLoadFinished = false;
        this.setAllEventsLoaded(true);
        if (this.connectionHandler.byEventId[requestType]) {
            this.connectionHandler.byEventId[requestType].deactivate();
        }
        const onAdd = (response) => this.processAddResponse(response, requestType);
        this.connectionHandler.byEventId[requestType] = this.connectonFactory.createEventsByEventId({
            onAdd,
            onChange: this.processChangeResponse,
            onInitial: onAdd,
            onDelete: this.processDeleteResponse,
            onError: this.processError
        });
        this.connectionHandler.byEventId[requestType].loadData(activeRequest);
    }
    /**
     * Request events by country
     */
    loadDataByRegionId(requestType) {
        let activeRequest = this.activeRequests[requestType].request;
        let loader = this.subLoaders[requestType];
        loader.isFirstLoad && (loader.isLoading = true);
        loader.isFullLoadFinished = false;
        this.setAllEventsLoaded(true);
        if (this.connectionHandler.byRegionId[requestType]) {
            this.connectionHandler.byRegionId[requestType].deactivate();
        }
        const onAdd = (response) => this.processAddResponse(response, requestType);
        this.connectionHandler.byRegionId[requestType] = this.connectonFactory.createEventsByRegionId({
            onAdd,
            onChange: this.processChangeResponse,
            onInitial: onAdd,
            onDelete: this.processDeleteResponse,
            onError: this.processError
        });
        this.connectionHandler.byRegionId[requestType].loadData(activeRequest);
    }
    /**
    * Request events by league ids
    */
    loadDataByLeagueIds(requestType) {
        let activeRequest = this.activeRequests[requestType].request;
        let loader = this.subLoaders[requestType];
        loader.isFirstLoad && (loader.isLoading = true);
        loader.isFullLoadFinished = false;
        this.setAllEventsLoaded(true);
        if (this.connectionHandler.byLeagueId[requestType]) {
            this.connectionHandler.byLeagueId[requestType].deactivate();
        }
        const onAdd = (response) => this.processAddResponse(response, requestType);
        this.connectionHandler.byLeagueId[requestType] = this.connectonFactory.createEventsByLeagueId({
            onAdd,
            onChange: this.processChangeResponse,
            onInitial: onAdd,
            onDelete: this.processDeleteResponse,
            onError: this.processError
        });
        this.connectionHandler.byLeagueId[requestType].loadData(activeRequest);
    }
    /**
     * Request events by personalization
     */
    loadDataByPersonalization(requestType) {
        const activeRequest = this.activeRequests[requestType].request;
        const loader = this.subLoaders[requestType];
        loader.isLoading = true;
        loader.isFullLoadFinished = false;
        this.setAllEventsLoaded(true);
        this.bff.getPersonalizedPreLiveEventsBySportId(activeRequest)
            .then(initialResponse => {
            if (this.isValidResponse(this.activeRequests[requestType].request, initialResponse, requestType)) {
                return;
            }
            const response = Object.assign(Object.assign({}, initialResponse), { leagues: [] });
            requestType === ActiveRequestsType.outrights
                ? this.updateRetrieverData(activeRequest, response, requestType)
                : this.buildUpdateQueryByPersonalization(response, requestType);
            this.updateWithTemporaryData(activeRequest, requestType);
            this.setLoader(false, requestType);
        }).catch(this.processError);
    }
    /**
     * Request events by coupon id
     */
    loadDataByCouponId(requestType) {
        const activeRequest = this.activeRequests[requestType].request;
        const loader = this.subLoaders[requestType];
        loader.isFirstLoad && (loader.isLoading = true);
        loader.isFullLoadFinished = false;
        this.setAllEventsLoaded(true);
        if (this.connectionHandler.byCouponId[requestType]) {
            this.connectionHandler.byCouponId[requestType].deactivate();
        }
        const onAdd = (response) => this.processAddResponse(response, requestType);
        this.connectionHandler.byCouponId[requestType] = this.connectonFactory.createEventsByCouponId({
            onAdd,
            onChange: this.processChangeResponse,
            onInitial: onAdd,
            onDelete: this.processDeleteResponse,
            onError: this.processError
        });
        this.connectionHandler.byCouponId[requestType].loadData(activeRequest);
    }
    loadMoreData() {
        Object.keys(this.activeRequests).forEach(type => {
            let request = this.activeRequests[type].request;
            this.setPagination(request, this.eventsPerRequest, request.pagination && request.pagination.skip
                ? request.pagination.skip + this.eventsPerRequest
                : this.eventsPerRequest);
        });
        if (this.sportsWithAdditionalEvents.indexOf(this.currentSports[0]) === -1) {
            this.sportsWithAdditionalEvents.push(this.currentSports[0]);
        }
        this.isLoadMore = true;
        this.loadData();
    }
    addDataBySportId(response, requestType) {
        this.addEventData(response.events || []);
        this.addMarketData(response.markets || []);
        if (response.leagues && response.leagues.length) {
            this.replaceLeagues({ leagues: response.leagues });
        }
        this.setLoader(false, requestType);
    }
    addEventData(events) {
        this.handleAdditionalEvents(events);
        events.forEach(event => {
            this.data.events.set(event.id, event);
        });
    }
    addMarketData(markets) {
        markets.forEach(market => {
            this.data.markets.set(market.id, market);
        });
    }
    processAddResponse(response, requestType) {
        const activeRequest = this.activeRequests[requestType].request;
        this.updateRetrieverData(activeRequest, response, requestType);
        this.updateWithTemporaryData(activeRequest, requestType);
        this.setLoader(false, requestType);
    }
    processChangeResponse(response) {
        if (response.events && response.markets) {
            this.change({ events: response.events, markets: response.markets });
        }
    }
    processDeleteResponse(response) {
        if (response.events && response.markets) {
            this.remove({ events: response.events, markets: response.markets });
        }
    }
    checkLoadedEvents(events) {
        if (events && events.length >= this.eventsPerRequest) {
            this.setAllEventsLoaded(false);
        }
    }
    handleAdditionalEvents(events) {
        if (this.isLoadMore) {
            events.forEach(event => {
                this.additionalEventIds.push(event.id);
            });
            this.isLoadMore = false;
        }
        else if (this.shouldClearAdditionalEvents && this.additionalEventIds.length) {
            this.additionalEventIds.forEach(id => {
                this.data.events.delete(id);
            });
            this.additionalEventIds = [];
        }
        this.shouldClearAdditionalEvents = false;
    }
    updateWithTemporaryData(request, requestType) {
        if (!this.subLoaders[requestType].isFirstLoad) {
            let filterFn = this.getFilterFunction(request, requestType);
            this.temporaryData.leagues = uniqueElementsById(this.temporaryData.leagues);
            this.updateCurrentData(this.temporaryData, filterFn, requestType);
        }
        this.subLoaders[requestType].isFullLoadFinished = true;
        this.subLoaders[requestType].isFirstLoad = false;
        if (!this.loader().isFirstLoad && this.loader().isFullLoadFinished) {
            this.temporaryData = { events: [], markets: [], leagues: [] };
        }
    }
    clearPagination() {
        Object.keys(this.activeRequests).forEach(type => {
            this.setPagination(this.activeRequests[type].request, this.eventsPerRequest, 0);
        });
    }
    setAllEventsLoaded(loaded) {
        this.allEventsLoaded = loaded;
    }
    setPagination(request, top, skip) {
        requestBuilder.buildPagination(request, { top, skip });
    }
    /**
     * Sets loader props
     * @param {boolean} isRequestInProcess Is request in process
     */
    setLoader(isRequestInProcess, type) {
        this.subLoaders[type].isLoading = isRequestInProcess;
        this.subLoaders[type].isFullLoadFinished = !isRequestInProcess;
    }
    /**
     * Process error from bff request
     */
    processError(error) {
        Object.keys(this.subLoaders).forEach(type => {
            this.setLoader(false, type);
        });
        // TODO: Process error
        console["error"](error);
    }
    /**
     * Update current data and subscribe for its updates
     * @param {EventListPresenterRequest} request Current request
     * @param {EventsResponse} response Received response
     */
    //It never receives requestType = "outrights" as we are not subscribing fot outright updates
    updateData(request, response, requestType) {
        // Get the current filtering function and pass in
        this.updateRetrieverData(request, response, requestType);
        // Need to understand how we are going to pull more than 100 items
        // Probably going to be solved on the BFF
        // sub to query only after it arrives
        // missing some of the changes that happened during the pull
        // SDAPI team is working on improving performance of the pull
        this.querySub();
    }
    updateTemporaryData(response) {
        Object.keys(this.temporaryData).forEach(collection => {
            this.temporaryData[collection] = this.temporaryData[collection].concat(response[collection]);
        });
    }
    /**
     * Set active BFF request to request by event ids
     * @param {boolean} isDefault Is default events request from data provider or not
     */
    setActiveRequest(type, isDefault = true) {
        let defaultRequest = (type === ActiveRequestsType.fixtures
            ? Object.assign({}, this.dataProvider.eventsRequest) : Object.assign({}, this.dataProvider.eventsRequestOutrights));
        this.activeRequests[type] = isDefault ? defaultRequest : {
            request: {
                eventTypes: defaultRequest && defaultRequest.request ? defaultRequest.request.eventTypes : []
            },
            type: EventsRequestType.ByEventIds
        };
    }
    /**
     * Get query params for query to SportData API
     */
    getQueryParams(type) {
        return {
            request: this.activeRequests[type].request,
            messageHandler: this.regularUpdateTimeOut,
        };
    }
    regularUpdateTimeOut(x) {
        setTimeout(this.regularUpdate.bind(this), 0, x);
    }
    /**
     * Get connection request params for query to data-layer
     */
    getRequestParams(type) {
        return {
            request: this.activeRequests[type].request,
            cardConfiguration: this.cardConfig.getConfig(this.isMiniEventCard),
            allSportIds: this.dataProvider.allSportIds
        };
    }
    initializeRequests() {
        let requests = {};
        if (this.dataProvider.eventsRequest) {
            requests.fixtures = Object.assign({}, this.dataProvider.eventsRequest);
            this.setPagination(requests.fixtures.request, this.eventsPerRequest, 0);
        }
        if (this.dataProvider.eventsRequestOutrights) {
            requests.outrights = Object.assign({}, this.dataProvider.eventsRequestOutrights);
            this.setPagination(requests.outrights.request, this.eventsPerRequest, 0);
        }
        return requests;
    }
    initializeLoaders() {
        let fixturesloader = this.dataProvider.eventsRequest
            ? { isLoading: true, isFirstLoad: true, isFullLoadFinished: false }
            : { isLoading: false, isFirstLoad: true, isFullLoadFinished: true };
        let outrightsloader = this.dataProvider.eventsRequestOutrights
            ? { isLoading: true, isFirstLoad: true, isFullLoadFinished: false }
            : { isLoading: false, isFirstLoad: true, isFullLoadFinished: true };
        return {
            fixtures: fixturesloader,
            outrights: outrightsloader
        };
    }
    isRequestAvailable() {
        return (this.activeRequests.fixtures && this.isIdsAvailable(this.activeRequests.fixtures.request)) ||
            (this.activeRequests.outrights && this.isIdsAvailable(this.activeRequests.outrights.request));
    }
    isIdsAvailable(request) {
        return (request.ids && request.ids.length) || request.sportId;
    }
    loader() {
        return {
            isLoading: this.subLoaders.fixtures.isLoading || this.subLoaders.outrights.isLoading,
            isFirstLoad: this.subLoaders.fixtures.isFirstLoad && this.subLoaders.outrights.isFirstLoad,
            isFullLoadFinished: this.subLoaders.fixtures.isFullLoadFinished && this.subLoaders.outrights.isFullLoadFinished
        };
    }
    updateRetrieverData(request, response, requestType) {
        let filterFn = this.getFilterFunction(request, requestType);
        this.subLoaders[requestType].isFirstLoad ? this.updateCurrentData(response, filterFn, requestType) : this.updateTemporaryData(response);
    }
    getFilterIds(request, requestType) {
        if (this.activeRequests[requestType].type === EventsRequestType.ByLeagueIds) {
            return this.currentSports;
        }
        if (this.activeRequests[requestType].type === EventsRequestType.ByRegionId) {
            return [request.sportId];
        }
        return request.ids;
    }
    isValidResponse(activeRequest, response, requestType) {
        const isCurrentRequest = activeRequest.ids ? !compareArrays(activeRequest.ids, this.activeRequests[requestType].request.ids) : false;
        return isCurrentRequest || isDifferentResponse(activeRequest, response);
    }
    finishLoading() {
        Object.keys(this.activeRequests).forEach(type => {
            this.setLoader(false, type);
        });
    }
    shouldBuildSortParam() {
        return this.cmsConfig.MainGroupBy !== MainGroupConfiguration.League && this.cmsConfig.SubGroupBy !== SubGroupConfiguration.League;
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], Retriever.prototype, "data", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], Retriever.prototype, "subLoaders", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], Retriever.prototype, "isMiniEventCard", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], Retriever.prototype, "allEventsLoaded", void 0);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], Retriever.prototype, "activate", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], Retriever.prototype, "deactivate", null);
__decorate([
    computed,
    __metadata("design:type", Array),
    __metadata("design:paramtypes", [])
], Retriever.prototype, "leagues", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Array]),
    __metadata("design:returntype", void 0)
], Retriever.prototype, "querySportUpdate", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, Array]),
    __metadata("design:returntype", void 0)
], Retriever.prototype, "queryDatesUpdate", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Array, Array]),
    __metadata("design:returntype", void 0)
], Retriever.prototype, "queryLeaguesUpdate", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String, String]),
    __metadata("design:returntype", void 0)
], Retriever.prototype, "queryRegionIdUpdate", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Number]),
    __metadata("design:returntype", void 0)
], Retriever.prototype, "queryTagsUpdate", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Array]),
    __metadata("design:returntype", void 0)
], Retriever.prototype, "queryFavoritesUpdate", null);
__decorate([
    Debounce(UpdateDebounceConfig),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], Retriever.prototype, "applyChanges", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], Retriever.prototype, "replaceLeagues", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, Function, String]),
    __metadata("design:returntype", void 0)
], Retriever.prototype, "updateCurrentData", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], Retriever.prototype, "remove", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], Retriever.prototype, "batchedChange", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, String]),
    __metadata("design:returntype", void 0)
], Retriever.prototype, "addDataBySportId", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Boolean]),
    __metadata("design:returntype", void 0)
], Retriever.prototype, "setAllEventsLoaded", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Boolean, String]),
    __metadata("design:returntype", void 0)
], Retriever.prototype, "setLoader", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], Retriever.prototype, "updateTemporaryData", null);
//# sourceMappingURL=retriever.js.map